import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  FileText,
  CheckCircle2,
  XCircle,
  Wand2,
  Eye,
  Search,
  Filter,
  RefreshCw,
  FileWarning,
  FileMinus,
  FileX,
  FileQuestion,
  FileClock,
  Building2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { mockClientsService as clientsService } from '@/services/api/mock-clients-data';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { DocumentInconsistency, InconsistencyType, InconsistencySeverity } from '@/types/client';

const inconsistencyTypeConfig: Record<InconsistencyType, { label: string; icon: React.ReactNode; color: string }> = {
  missing_document: {
    label: 'Missing Document',
    icon: <FileMinus className="h-4 w-4" />,
    color: 'text-red-500',
  },
  data_mismatch: {
    label: 'Data Mismatch',
    icon: <FileWarning className="h-4 w-4" />,
    color: 'text-orange-500',
  },
  cross_year_inconsistency: {
    label: 'Cross-Year Inconsistency',
    icon: <FileX className="h-4 w-4" />,
    color: 'text-amber-500',
  },
  missing_supporting_doc: {
    label: 'Missing Supporting Doc',
    icon: <FileQuestion className="h-4 w-4" />,
    color: 'text-yellow-500',
  },
  incomplete_set: {
    label: 'Incomplete Set',
    icon: <FileText className="h-4 w-4" />,
    color: 'text-blue-500',
  },
  expired_reference: {
    label: 'Expired Reference',
    icon: <FileClock className="h-4 w-4" />,
    color: 'text-purple-500',
  },
};

const severityConfig: Record<InconsistencySeverity, { label: string; className: string }> = {
  critical: { label: 'Critical', className: 'bg-red-500/10 text-red-600 border-red-500/30' },
  high: { label: 'High', className: 'bg-orange-500/10 text-orange-600 border-orange-500/30' },
  medium: { label: 'Medium', className: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  low: { label: 'Low', className: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
};

export function DocumentConsistencyPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<InconsistencyType | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<InconsistencySeverity | 'all'>('all');
  const [showResolved, setShowResolved] = useState(false);

  // Fetch inconsistencies
  const { data: inconsistenciesData, isLoading } = useQuery({
    queryKey: ['document-inconsistencies', typeFilter, severityFilter, showResolved, searchQuery],
    queryFn: () => clientsService.getDocumentInconsistencies({
      type: typeFilter !== 'all' ? typeFilter : undefined,
      severity: severityFilter !== 'all' ? severityFilter : undefined,
      resolved: showResolved,
      search: searchQuery || undefined,
    }),
  });

  const inconsistencies = inconsistenciesData?.inconsistencies || [];
  const stats = inconsistenciesData?.stats;

  // Resolve inconsistency mutation
  const resolveMutation = useMutation({
    mutationFn: (id: string) => clientsService.resolveInconsistency(id),
    onSuccess: () => {
      toast.success('Inconsistency marked as resolved');
      queryClient.invalidateQueries({ queryKey: ['document-inconsistencies'] });
    },
  });

  // Auto-fix mutation
  const autoFixMutation = useMutation({
    mutationFn: (id: string) => clientsService.autoFixInconsistency(id),
    onSuccess: () => {
      toast.success('Auto-fix applied successfully');
      queryClient.invalidateQueries({ queryKey: ['document-inconsistencies'] });
    },
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Document Consistency Checker</h1>
          <p className="text-muted-foreground">
            AI-powered detection of missing and inconsistent documents across client files
          </p>
        </div>
        <Button variant="outline">
          <RefreshCw className="mr-2 h-4 w-4" />
          Run Check
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-9 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.totalClients}</p>
                <p className="text-xs text-muted-foreground mt-1">Total Clients</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.clientsWithIssues}</p>
                <p className="text-xs text-muted-foreground mt-1">With Issues</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.totalInconsistencies}</p>
                <p className="text-xs text-muted-foreground mt-1">Total Issues</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{stats.criticalIssues}</p>
                <p className="text-xs text-muted-foreground mt-1">Critical</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">{stats.highIssues}</p>
                <p className="text-xs text-muted-foreground mt-1">High</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.mediumIssues}</p>
                <p className="text-xs text-muted-foreground mt-1">Medium</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.lowIssues}</p>
                <p className="text-xs text-muted-foreground mt-1">Low</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-purple-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{stats.autoFixableIssues}</p>
                <p className="text-xs text-muted-foreground mt-1">Auto-Fixable</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{stats.resolvedIssues}</p>
                <p className="text-xs text-muted-foreground mt-1">Resolved</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search clients or issues..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as InconsistencyType | 'all')}>
                <SelectTrigger>
                  <SelectValue placeholder="Issue Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(inconsistencyTypeConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Select value={severityFilter} onValueChange={(v) => setSeverityFilter(v as InconsistencySeverity | 'all')}>
                <SelectTrigger>
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant={showResolved ? "default" : "outline"}
              onClick={() => setShowResolved(!showResolved)}
            >
              {showResolved ? 'Hide' : 'Show'} Resolved
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Inconsistencies List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Detected Inconsistencies</CardTitle>
          <CardDescription>
            {inconsistencies.length} inconsistenc{inconsistencies.length !== 1 ? 'ies' : 'y'} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : inconsistencies.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
              <p className="font-medium">No inconsistencies found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {searchQuery || typeFilter !== 'all' || severityFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'All client files are consistent'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {inconsistencies.map((issue: DocumentInconsistency) => (
                <div
                  key={issue.id}
                  className={cn(
                    'p-4 rounded-lg border transition-colors',
                    issue.resolved ? 'bg-green-500/5 border-green-500/20' : 'bg-card hover:bg-muted/50'
                  )}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={inconsistencyTypeConfig[issue.type].color}>
                          {inconsistencyTypeConfig[issue.type].icon}
                        </div>
                        <h3
                          className="font-semibold cursor-pointer hover:text-primary"
                          onClick={() => navigate(`/panel/files/clients/${issue.clientId}`)}
                        >
                          {issue.clientName}
                        </h3>
                        <Badge variant="outline" className="text-xs">{issue.clientType}</Badge>
                        <Badge variant="outline" className={cn('text-xs', inconsistencyTypeConfig[issue.type].color)}>
                          {inconsistencyTypeConfig[issue.type].label}
                        </Badge>
                        <Badge variant="outline" className={cn('text-xs', severityConfig[issue.severity].className)}>
                          {severityConfig[issue.severity].label}
                        </Badge>
                        {issue.autoFixable && (
                          <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/30">
                            Auto-Fixable
                          </Badge>
                        )}
                        {issue.resolved && (
                          <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/30 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Resolved
                          </Badge>
                        )}
                      </div>
                      <h4 className="font-medium mb-1">{issue.title}</h4>
                      <p className="text-sm text-muted-foreground mb-2">{issue.description}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Detected: {formatDate(issue.detectedAt)}</span>
                        {issue.resolvedAt && (
                          <span className="text-green-600">Resolved: {formatDate(issue.resolvedAt)}</span>
                        )}
                      </div>
                    </div>
                    {!issue.resolved && (
                      <div className="flex gap-2">
                        {issue.autoFixable && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => autoFixMutation.mutate(issue.id)}
                            disabled={autoFixMutation.isPending}
                          >
                            <Wand2 className="mr-2 h-4 w-4" />
                            Auto-Fix
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resolveMutation.mutate(issue.id)}
                          disabled={resolveMutation.isPending}
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Resolve
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/panel/files/clients/${issue.clientId}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 text-sm">
                    {issue.affectedDocuments.length > 0 && (
                      <div>
                        <p className="font-medium mb-1">Affected Documents:</p>
                        <div className="flex flex-wrap gap-1">
                          {issue.affectedDocuments.map((doc, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">{doc}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {issue.expectedDocuments && issue.expectedDocuments.length > 0 && (
                      <div>
                        <p className="font-medium mb-1">Expected Documents:</p>
                        <div className="flex flex-wrap gap-1">
                          {issue.expectedDocuments.map((doc, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs text-amber-600">{doc}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <p className="font-medium mb-1">Recommendation:</p>
                      <p className="text-muted-foreground">{issue.recommendation}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
